import { useEffect, useMemo } from 'react';
import { useQueryClient, UseQueryResult } from 'react-query';

import {
  GetSuggestionsQueryParams,
  getUseGetSuggestionsKey,
  useGetSuggestions,
  type FieldSuggestion,
  PartWorkerTasks,
  TaskResolution,
  TaskStatus,
  RelatedEntityType,
} from '@frontend/api';
import { useMemoizedArray } from '@frontend/utils';

import {
  AutoWatchTasksDescriptor,
  useTaskStatusTracking,
} from './useTaskStatusTracking';

/**
 * This hook is used to watch for part descriptor suggestions updates and invalidate the
 * suggestions query when the part descriptor suggestions are updated.
 * @param partUuid - The uuid of the part to watch for part descriptor suggestions updates.
 * @param memoizedGetSuggestionsParams - The memoized get suggestions params.
 * @returns The suggestions data.
 */
export const useLivePartDescriptorSuggestions = ({
  partUuid,
  partFileUuids,
  shouldEnableQueries = true,
}: {
  partUuid: string;
  partFileUuids: string[];
  shouldEnableQueries?: boolean;
}): UseQueryResult<FieldSuggestion[], unknown> => {
  const memoizedPartFileUuids = useMemoizedArray(partFileUuids);
  const memoizedGetSuggestionsParams = useMemo(() => {
    return {
      queryOptions: {
        enabled: shouldEnableQueries && memoizedPartFileUuids.length > 0,
        staleTime: 1000 * 60 * 5,
      },
      query: {
        sourcePartFileId: memoizedPartFileUuids,
      },
    };
  }, [memoizedPartFileUuids, shouldEnableQueries]);

  const suggestionsData = useGetSuggestions(memoizedGetSuggestionsParams);

  const queryClient = useQueryClient();
  const { registerAutoWatchCallback, unregisterAutoWatchCallback } =
    useTaskStatusTracking();

  useEffect(() => {
    const queryKey = getUseGetSuggestionsKey(
      memoizedGetSuggestionsParams?.query as GetSuggestionsQueryParams,
    );

    const descriptor: AutoWatchTasksDescriptor = {
      name: 'part-descriptor-results',
      sourceEntityType: RelatedEntityType.PART,
      sourceEntityPk: partUuid,
      tasks: [PartWorkerTasks.PRINT_PAGE_PARTBOT_PROCESSING],
      statuses: [TaskStatus.COMPLETED],
      resolutions: [TaskResolution.SUCCESS],
    };

    registerAutoWatchCallback({
      callback: () => {
        queryClient.invalidateQueries(queryKey);
      },
      descriptor,
    });

    return () => unregisterAutoWatchCallback(descriptor);
  }, [
    memoizedGetSuggestionsParams?.query,
    partUuid,
    queryClient,
    registerAutoWatchCallback,
    unregisterAutoWatchCallback,
  ]);

  return suggestionsData;
};
